import { useEffect } from 'react';
import { getSessionToken } from './api.js';
import { useSession } from './session.js';

export function isRecentForegroundActivity(visible: boolean, now: number, lastInteraction: number) {
  return visible && now >= lastInteraction && now - lastInteraction < 90_000;
}

export async function postStatisticsPulse(active: boolean, signal?: AbortSignal) {
  const token = getSessionToken();
  if (!token) return;
  return fetch('/api/v1/me/statistics/pulse', {
    method: 'POST',
    credentials: 'omit',
    signal: signal ?? null,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ active }),
  });
}

export function ActivityStatistics() {
  const userId = useSession((state) => state.user?.id);
  const deletion = useSession((state) => state.deletionPhase);
  useEffect(() => {
    if (!userId || deletion !== 'NONE') return;
    let lastInteraction = Date.now();
    let pending = false;
    let stopped = false;
    let controller: AbortController | undefined;
    const interact = () => {
      lastInteraction = Date.now();
    };
    const send = async (active: boolean) => {
      if (stopped || pending || !getSessionToken() || document.visibilityState !== 'visible')
        return;
      pending = true;
      controller = new AbortController();
      const timeout = window.setTimeout(() => controller?.abort(), 10_000);
      try {
        // Do not use the shared API error channel: an optional telemetry 429 must
        // not impose a global client cooldown on the chat/composer.
        await postStatisticsPulse(active, controller.signal);
      } catch {
        // Optional measurements must never hide a chat or trigger retry storms.
      } finally {
        window.clearTimeout(timeout);
        pending = false;
      }
    };
    void send(false);
    const visible = () => {
      if (document.visibilityState === 'visible') {
        interact();
        void send(false);
      }
    };
    const tick = window.setInterval(() => {
      if (
        isRecentForegroundActivity(
          document.visibilityState === 'visible',
          Date.now(),
          lastInteraction,
        )
      )
        void send(true);
    }, 30_000);
    document.addEventListener('visibilitychange', visible);
    for (const event of ['pointerdown', 'keydown', 'scroll'])
      document.addEventListener(event, interact, { passive: true, capture: true });
    return () => {
      stopped = true;
      controller?.abort();
      window.clearInterval(tick);
      document.removeEventListener('visibilitychange', visible);
      for (const event of ['pointerdown', 'keydown', 'scroll'])
        document.removeEventListener(event, interact, { capture: true });
    };
  }, [userId, deletion]);
  return null;
}
